/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { useGlobalContext } from "context/globalContext";
import { IoMdClose } from "react-icons/io";
import { LLM_CONSTANTS } from "constants/configCategory";
import {
  createOrUpdatePromptFragmentsAPI,
  getAutoSuggestAPI,
} from "Api/promptFragments";
import PromptFragmentsValueTextArea from "./components/promptFragmentsValueTextArea/promptFragmentsValueTextArea";

const CreateOrUpdatePromptFragmentsModal = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode,
}) => {
  const {
    promptFragmentsFormData,
    setPromptFragmentsFormData,
    initialPromptFragmentsFormState,
    setFetchPromptFragments,
  } = useGlobalContext();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [originalFormData, setOriginalFormData] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [debounceTimer, setDebounceTimer] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [activeSuggestionField, setActiveSuggestionField] = useState("");

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.height = "100%";
      setOriginalFormData(promptFragmentsFormData);
    } else {
      document.body.style.overflow = "";
      document.body.style.height = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.height = "";
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let updatedData = {
      ...promptFragmentsFormData,
      [name]: value,
    };

    if (name === "name" && value.trim() === "") {
      updatedData.key = "";
    }

    if (name === "key" && value.trim() === "") {
      updatedData.name = "";
    }

    setPromptFragmentsFormData(updatedData);

    if (name === "key") {
      setErrorMessage("");
    }

    if ((name === "name" || name === "key") && value.trim().length > 2) {
      clearTimeout(debounceTimer);
      const timer = setTimeout(() => fetchSuggestions(value, name), 300);
      setDebounceTimer(timer);
    } else if (name === "name" || name === "key") {
      setSuggestions([]);
    }
  };

  const fetchSuggestions = async (query, field) => {
    setLoadingSuggestions(true);
    setActiveSuggestionField(field);
    try {
      const token = localStorage.getItem("jwtToken");
      const response = await getAutoSuggestAPI(
        query,
        field,
        setLoadingSuggestions,
        token
      );
      setSuggestions(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error(`Error fetching ${field} suggestions:`, err);
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const isFormValid = useCallback(() => {
    const { name, key, value, LLM } = promptFragmentsFormData;
    return Boolean(name?.trim() && key?.trim() && value?.trim() && LLM);
  }, [promptFragmentsFormData]);

  const hasChanges = useCallback(() => {
    return (
      JSON.stringify(promptFragmentsFormData) !==
      JSON.stringify(originalFormData)
    );
  }, [promptFragmentsFormData, originalFormData]);

  const handleFormDataWithTextAreaValue = useCallback(
    (newTextValue) => {
      setPromptFragmentsFormData({
        ...promptFragmentsFormData,
        value: newTextValue,
      });
    },
    [promptFragmentsFormData, setPromptFragmentsFormData]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;

    setIsSubmitting(true);
    const token = localStorage.getItem("jwtToken");

    const response = await createOrUpdatePromptFragmentsAPI(
      {
        ...promptFragmentsFormData,
        isActive: promptFragmentsFormData.isActive === "active",
      },
      setIsSubmitting,
      token
    );

    if (response?.success) {
      const successMessage = promptFragmentsFormData?.id
        ? "Prompt fragment updated successfully"
        : "Prompt fragment created successfully";

      toast.success(successMessage);
      onSubmit(response.data);
      onClose();
      setPromptFragmentsFormData(initialPromptFragmentsFormState);
      setFetchPromptFragments(true);
    } else {
      toast.error(
        response?.message || "Error creating/updating prompt fragment."
      );
    }
  };

  const fetchNameSuggestions = async (query) => {
    setLoadingSuggestions(true);
    try {
      const accessJWT = localStorage.getItem("jwtToken");
      const response = await getAutoSuggestAPI(
        query,
        "name",
        setLoadingSuggestions,
        accessJWT
      );
      setSuggestions(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error fetching name suggestions:", error);
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleCancel = () => {
    setPromptFragmentsFormData(initialPromptFragmentsFormState);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="bg-black fixed inset-0 z-50 flex h-screen items-center justify-center overscroll-contain bg-opacity-50 ">
      <div className="relative z-50 mx-auto flex max-h-[95dvh] w-4/5 flex-col items-center justify-start overflow-auto rounded-xl bg-white p-6 shadow-xl dark:!bg-gray-10 ">
        <button
          type="button"
          onClick={handleCancel}
          className="absolute top-6 right-6 text-gray-500 hover:text-gray-600 focus:outline-none dark:hover:text-gray-100 "
        >
          <IoMdClose size={24} />
        </button>

        <form onSubmit={handleSubmit} className="w-full ">
          <div className="mt-4 flex justify-center gap-4">
            <div className="w-1/2">
              <label className="mb-2 block font-bold text-gray-700 dark:text-white">
                <span className="text-red-600">*</span> Name:
              </label>
              <input
                type="text"
                name="name"
                autoComplete="off"
                value={promptFragmentsFormData?.name}
                onChange={handleChange}
                className="focus:border-transparent h-11 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
                required
              />
              {suggestions.length > 0 && activeSuggestionField === "name" && (
                <ul className="mt-2 max-h-60 overflow-y-auto rounded-md border bg-white shadow-lg dark:!bg-gray-10">
                  {suggestions.map((s, i) => (
                    <li
                      key={i}
                      onClick={() => {
                        setPromptFragmentsFormData((prev) => ({
                          ...prev,
                          name: s.name,
                          key: s.key,
                        }));
                        setSuggestions([]);
                      }}
                      className="cursor-pointer px-4 py-2 hover:bg-gray-200 dark:text-white"
                    >
                      {s.name}
                    </li>
                  ))}
                </ul>
              )}

              {loadingSuggestions && activeSuggestionField === "name" && (
                <p className="mt-1 text-sm text-gray-500 dark:text-white">
                  Loading...
                </p>
              )}

              {errorMessage && (
                <p className="mt-2 text-sm text-red-500">{errorMessage}</p>
              )}
            </div>
            <div className="w-1/2">
              <label className="mb-2 block font-bold text-gray-700 dark:text-white">
                <span className="text-red-600">*</span> Key:
              </label>
              <input
                type="text"
                name="key"
                 autoComplete="off"
                value={promptFragmentsFormData?.key}
                disabled={isEditMode}
                onChange={handleChange}
                className="focus:border-transparent h-11 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500  dark:!bg-gray-10  dark:text-white"
                required
              />
              {suggestions.length > 0 && activeSuggestionField === "key" && (
                <ul className="mt-2 max-h-60 overflow-y-auto rounded-md border bg-white shadow-lg dark:!bg-gray-10">
                  {suggestions.map((s, i) => (
                    <li
                      key={i}
                      onClick={() => {
                        setPromptFragmentsFormData((prev) => ({
                          ...prev,
                          key: s.key,
                          name: s.name,
                        }));
                        setSuggestions([]);
                      }}
                      className="cursor-pointer px-4 py-2 hover:bg-gray-200 dark:text-white"
                    >
                      {s.key}
                    </li>
                  ))}
                </ul>
              )}

              {errorMessage && (
                <p className="mt-2 text-sm text-red-500">{errorMessage}</p>
              )}
            </div>
          </div>

          <div className="mt-4 flex justify-center gap-4">
            <div className="w-1/2">
              <label className="mb-2 block font-bold text-gray-700 dark:text-white">
                <span className="text-red-600">*</span> Status:
              </label>
              <div className="justify-right flex items-center">
                <label className="mr-4   dark:text-white">
                  <input
                    type="radio"
                    name="isActive"
                    value="active"
                    checked={promptFragmentsFormData.isActive === "active"}
                    onChange={handleChange}
                    className="relative top-0.5 mr-1 cursor-pointer"
                  />
                  Active
                </label>
                <label className="  dark:text-white">
                  <input
                    type="radio"
                    name="isActive"
                    value="inactive"
                    checked={promptFragmentsFormData.isActive === "inactive"}
                    onChange={handleChange}
                    className="relative top-0.5 mr-1 cursor-pointer"
                  />
                  In-Active
                </label>
              </div>
            </div>

            <div className="w-1/2">
              <label className="mb-2 block font-bold text-gray-700 dark:text-white">
                <span className="text-red-600">*</span> LLM:
              </label>
              <div className="relative">
                <select
                  name="LLM"
                  onChange={handleChange}
                  value={promptFragmentsFormData.LLM}
                  className="focus:border-transparent h-11 w-full cursor-pointer appearance-none rounded-md border border-gray-300 px-3 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white "
                >
                  {LLM_CONSTANTS.map((config, idx) => (
                    <option key={idx} value={config.value}>
                      {config.type}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4">
                  <svg
                    className="fill-current h-4 w-4 text-gray-500"
                    viewBox="0 0 20 20"
                  >
                    <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-0 flex justify-start gap-4">
            <div className="w-1/2">
              <label className="mb-2 block font-bold text-gray-700  dark:text-white">
                Comment:
              </label>
              <textarea
                name="comment"
                value={promptFragmentsFormData.comment}
                onChange={handleChange}
                className="focus:border-transparent h-11 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
              ></textarea>
            </div>
          </div>

          <div className="mt-4 flex justify-center">
            <div className="w-full max-w-full">
              <label className="mb-2 block font-bold text-gray-700 dark:text-white">
                <span className="text-red-600">*</span> Value:
              </label>
              <PromptFragmentsValueTextArea
                handleChange={handleChange}
                setFormDataWithTextAreaValue={handleFormDataWithTextAreaValue}
                textAreaValue={promptFragmentsFormData?.value}
              />
            </div>
          </div>

          <div className="mt-3 flex justify-end space-x-4">
            <button
              type="submit"
              className={`rounded-md py-2 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${
                isSubmitting || !isFormValid() || (isEditMode && !hasChanges())
                  ? "cursor-not-allowed bg-gray-400"
                  : "bg-green-600 hover:bg-green-500"
              }`}
              disabled={
                isSubmitting || !isFormValid() || (isEditMode && !hasChanges())
              }
            >
              {isSubmitting ? "Submitting..." : isEditMode ? "Update" : "Add"}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-md bg-red-600 py-2 px-4 text-white hover:bg-red-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateOrUpdatePromptFragmentsModal;
